import { useCallback, useEffect, useState } from "react";
import BooleanInput from "./BooleanInput";
import DateInput from "./DateInput";
import NumberInput from "./NumberInput";
import SelectionInput from "./SelectionInput";
import TextAreaInput from "./TextAreaInput";
import TextInput from "./TextInput";
import CurrencyInput from "./CurrencyInput";
import { BsExclamationCircleFill, BsFillCameraFill, BsShieldExclamation } from "react-icons/bs";
import { FcInfo } from "react-icons/fc";
import CameraContainer from "./../camera/cameracontainer";
import { AiFillLock } from "react-icons/ai";
import ProjectErrorMessage from "../ProjectErrorMessage";
import { BiLink } from "react-icons/bi";
import { FaMapMarkerAlt } from "react-icons/fa";

const FieldConverter = ({
	conditionalData,
	setConditionalData,
	templateName,
	allFields,
	sectionId,
	projectId,
	templateId,
	fieldData,
	setFieldData,
	projectName,
	data,
	api,
	token,
	sectionName,
	stage,
	templateTypeId,
	odooMappings,
	odooId,
	setRequiredFieldsRemaining,
	showThisField,
	showThisSection,
	initialThumbnailPathes,
	setLastModifiedOn,
	selectedOdooModel,
	setLastUpdatedBy,
	odooInstallationName,
	odooParentName,
	isGuest,
	startingPendingImages,
	focusedFieldId,
	hasAerialView,
	hasAerialMarker,
	onShowOnAerial,
	onFieldDataChange,
	setAerialMarkersMissingRemaining,
	aerialMarkersReady,
}) => {
	const [fieldSettings, setFieldSettings] = useState([]);
	const [initialFieldData, setInitialFieldData] = useState();
	const [fieldDataExists, setFieldDataExists] = useState(false);
	const [hasConditional, setHasConditional] = useState(false);
	const [viewConditionMet, setViewConditionMet] = useState(false);
	const [currentOdooMappings, setCurrentOdooMappings] = useState([]);
	const [odooData, setOdooData] = useState("");
	const [comment, setComment] = useState("");
	const [hasPhotos, setHasPhotos] = useState(false);
	const [metRequireCondition, setMetRequiredCondition] = useState(true);
	const [metRequireCommentCondition, setMetRequiredCommentCondition] = useState(true);
	const [odooUpdateFailed, setOdooUpdateFailed] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [postingData, setPostingData] = useState(false);
	const [inSQS, setInSQS] = useState([]);

	useEffect(() => {
		let trimmedStrings = data.field_settings.split("[break]");
		for (let i = 0; i < trimmedStrings.length; i++) {
			trimmedStrings[i] = trimmedStrings[i].trim();
		}
		setFieldSettings(trimmedStrings);
	}, [data.field_settings]);

	const convertToYesNo = (theValue) => {
		let returnValue = "";
		if (theValue === 1 || theValue === "1" || (typeof theValue === "string" && theValue.trim().toLowerCase() === "yes") || theValue === true || (typeof theValue === "string" && theValue.trim().toLowerCase() === "true")) {
			returnValue = "yes";
		} else if (theValue === 0 || theValue === "0" || (typeof theValue === "string" && theValue.trim().toLowerCase() === "no") || theValue === false || (typeof theValue === "string" && theValue.trim().toLowerCase() === "false")) {
			returnValue = "no";
		}
		return returnValue;
	};

	useEffect(() => {
		setCurrentOdooMappings(odooMappings);

		if (fieldData) {
			for (let i = 0; i < fieldData.length; i++) {
				if (fieldData[i].field_id === data.id) {
					setInitialFieldData(fieldData[i]);
					setComment(fieldData[i].comment === null ? "" : fieldData[i].comment);
					break;
				}
			}
		}

		for (let i = 0; i < odooMappings.length; i++) {
			if (odooMappings[i].templatefieldid === data.id && odooMappings[i].odoo_value !== "") {
				setOdooData(odooMappings[i]);
			}
		}
	}, [fieldData, data, odooMappings]);

	useEffect(() => {
		if (typeof initialFieldData !== "undefined") {
			setFieldDataExists(true);
		}
	}, [initialFieldData]);

	const checkConditionalMet = useCallback(
		(idOfFieldToVerify) => {
			let conditionalList = "";
			for (let i = 0; i < allFields.length; i++) {
				if (allFields[i].id.toString() === idOfFieldToVerify.toString()) {
					if (allFields[i].conditional_list === "") {
						return true;
					} else {
						conditionalList = allFields[i].conditional_list.split(",");
						break;
					}
				}
			}

			for (let i = 0; i < conditionalList.length; i++) {
				for (let j = 0; j < fieldData.length; j++) {
					if (fieldData[j].field_id.toString() === conditionalList[i].toString()) {
						let valueOfFoundField = fieldData[j].value;
						let fieldType = fieldData[j].field_type;
						let conditionValueToCheck = conditionalList[i + 1].replaceAll("[comma1]", ",");
						if (odooMappings && odooMappings.length > 0) {
							let theCondField = odooMappings.filter((filterData) => filterData.templatefieldid.toString() === conditionalList[i].toString());
							if (theCondField.length > 0) {
								let foundFieldType = allFields.filter((filterData) => filterData.id.toString() === conditionalList[i].toString());
								if (foundFieldType[0].field_type === "yes/no") {
									valueOfFoundField = convertToYesNo(theCondField[0].odoo_value);
								} else {
									valueOfFoundField = theCondField[0].odoo_value;
								}
							}
						}
						if (fieldType === "yes/no") {
							conditionValueToCheck = convertToYesNo(conditionValueToCheck);
						}
						if (valueOfFoundField === conditionValueToCheck && checkConditionalMet(fieldData[j].field_id)) {
							return true;
						}
					}
				}
			}
			return false;
		},
		[allFields, fieldData, odooMappings]
	);

	useEffect(() => {
		if (typeof data.conditional_list !== "undefined" && data.conditional_list !== "") {
			setHasConditional(true);

			let conditionalListSplit = data.conditional_list.split(",");

			if (conditionalListSplit.length <= 1) {
				return;
			}

			let conditionMet = checkConditionalMet(data.id);
			if (conditionMet) {
				setViewConditionMet(true);
			} else {
				setViewConditionMet(false);
			}
		}
	}, [fieldData, data.id, checkConditionalMet, data.conditional_list]);

	useEffect(() => {
		if (((data.required || data.require_photos) && hasConditional && viewConditionMet) || (!hasConditional && (data.required || data.require_photos))) {
			let requiredConditionMet = true;
			let requiredPhotoConditionsMet = true;
			let requiredCommentConditionMet = true;

			if (data.required && data.field_type !== "photos") {
				if (data.odoo_mapping) { 
					if(initialFieldData && initialFieldData.field_type == "yes/no" && initialFieldData.value === "no"){ // /api/pms/mappings evaluates 'f' to "", odoo uses boolean value, which cannot map to yes/no type. 
						requiredConditionMet = true;
					}
					else if (!odooData || odooData.odoo_value === "" || odooData.odoo_value === "failedtogetdataforpmscode400") {
						requiredConditionMet = false;
					} else {
						requiredConditionMet = true;
					}
				} else {
					if (!initialFieldData || initialFieldData.value === "" || (initialFieldData && !initialFieldData.value)) {
						requiredConditionMet = false;
					} else {
						requiredConditionMet = true;
					}
				}
			}

			if (data.required && data.require_comment) {
				if (!initialFieldData || initialFieldData.comment === "") {
					requiredCommentConditionMet = false;
				} else {
					requiredCommentConditionMet = true;
				}
			}
			//if ((data.require_photos && !hasPhotos) || (data.required && data.field_type === "photos") || (data.allow_photos && !hasPhotos)) {
			if ((data.require_photos && !hasPhotos) || (data.required && data.field_type === "photos")) {
				if (!hasPhotos) {
					requiredPhotoConditionsMet = false;
				} else {
					requiredPhotoConditionsMet = true;
				}
			}

			if (!requiredConditionMet || !requiredPhotoConditionsMet || !requiredCommentConditionMet) {
				setRequiredFieldsRemaining((prev) => (prev.includes(data.id.toString()) === false ? [...prev, data.id.toString()] : prev));
				setMetRequiredCondition(requiredConditionMet);
				setMetRequiredCommentCondition(requiredCommentConditionMet);
			} else {
				setRequiredFieldsRemaining((prev) =>
					prev.includes(data.id.toString()) ? [...prev.filter((filterData) => filterData !== data.id.toString())] : prev
				);

				setMetRequiredCondition(requiredConditionMet);
				setMetRequiredCommentCondition(requiredCommentConditionMet);
			}
		} else {
			setRequiredFieldsRemaining((prev) =>
				prev.includes(data.id.toString()) ? [...prev.filter((filterData) => filterData !== data.id.toString())] : prev
			);
			setMetRequiredCondition(true);
			setMetRequiredCommentCondition(true);
		}
	}, [initialFieldData, data, hasConditional, hasPhotos, setRequiredFieldsRemaining, viewConditionMet, odooData]);

	useEffect(() => {
		if (!setAerialMarkersMissingRemaining) {
			return;
		}

		const fieldId = data.id.toString();
		const requiresMarker = Boolean(data.require_aerial_view_marker);
		const inScope = !hasConditional || viewConditionMet;
		const shouldWarn = Boolean(aerialMarkersReady) && requiresMarker && inScope && !hasAerialMarker;

		if (shouldWarn) {
			setAerialMarkersMissingRemaining((prev) => (prev.includes(fieldId) ? prev : [...prev, fieldId]));
		} else {
			setAerialMarkersMissingRemaining((prev) => (prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : prev));
		}
	}, [aerialMarkersReady, data, hasAerialMarker, hasConditional, setAerialMarkersMissingRemaining, viewConditionMet]);

	const postToDB = (valueToPost, setSendToDbFailed = undefined) => {
		if (postingData) {
			return;
		}
		setPostingData(true);
		fetch(api + "/projectdata", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify({
				field_id: data.id,
				value: valueToPost,
				project_id: projectId,
				section_id: sectionId,
				odoo_mapping: data.odoo_mapping,
				comment,
				odooId: odooId,
				projectId: projectId,
				odoo_model: selectedOdooModel,
				ox2_mapping: data.ox2_mapping,
			}),
		})
			.then((res) => {
				if (!res.ok) {
					setPostingData(false);
					// put an error here
					setErrorMessage(`Failed to update on Odoo:\n${sectionName} - ${data.name}`);
					setOdooUpdateFailed(true);
				} else {
					return res.json();
				}
			})
			.then((res) => {
				if (res) {
					setPostingData(false);
					setLastModifiedOn(new Date());
					setLastUpdatedBy(localStorage.getItem("username") ? localStorage.getItem("username") : "You");
					if (res.odooUpdateFailed) {
						setErrorMessage(`Failed to update on Odoo:\n${sectionName} - ${data.name}`);
						setOdooUpdateFailed(true);
					} else {
						setOdooUpdateFailed(false);
					}
					let newData = {
						id: res.projectDataId,
						field_id: data.id,
						value: valueToPost,
						project_id: projectId,
						section_id: sectionId,
						comment,
						odooId: odooId,
					};
					setInitialFieldData(newData);
					setFieldData([...fieldData, newData]);
					if (onFieldDataChange) {
						onFieldDataChange(data.id, { value: valueToPost, comment, projectFieldId: res.projectDataId });
					}
					if (data.odoo_mapping && data.odoo_mapping !== "") {
						let mappingsClone = [...odooMappings];
						for (let i = 0; i < mappingsClone.length; i++) {
							if (data.id === mappingsClone[i].templatefieldid) {
								mappingsClone[i].odoo_value = valueToPost;
								let newOdooData = { ...odooData };
								newOdooData["odoo_value"] = valueToPost;
								setOdooData(newOdooData);
								setCurrentOdooMappings([...mappingsClone]);
								break;
							}
						}
					}

					for (let i = 0; i < conditionalData.length; i++) {
						if (conditionalData[i].field_condition_id === newData.field_id) {
							let conditionMet = false;
							if (conditionalData[i].condition_type === ">=") {
								if (parseInt(newData.value) >= parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === ">") {
								if (parseInt(newData.value) > parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === "=") {
								if (newData.value == conditionalData[i].condition_value) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === "<") {
								if (parseInt(newData.value) < parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === "<=") {
								if (parseInt(newData.value) <= parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							}
							let copyOfData = conditionalData;
							copyOfData[i].conditionMet = conditionMet;
							setConditionalData([...copyOfData]);
						}
					}
				}
			});
	};

	const putToDB = (valueToPut, setSendToDbFailed = undefined) => {
		fetch(api + "/projectdata/" + initialFieldData.id, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify({
				value: valueToPut,
				comment,
				odoo_mapping: data.odoo_mapping,
				odooId: odooId,
				projectId: projectId,
				odoo_model: selectedOdooModel,
				ox2_mapping: data.ox2_mapping,
			}),
		})
			.then((res) => {
				if (!res.ok) {
					// put an error here
					setErrorMessage(`Failed to update on Odoo:\n${sectionName} - ${data.name}`);
					setOdooUpdateFailed(true);
				} else {
					return res.json();
				}
			})
			.then((res) => {
				if (res) {
					if (res.odooUpdateFailed) {
						setErrorMessage(`Failed to update on Odoo:\n${sectionName} - ${data.name}`);
						setOdooUpdateFailed(true);
					} else {
						setOdooUpdateFailed(false);
					}
					setLastUpdatedBy(localStorage.getItem("username") ? localStorage.getItem("username") : "You");
					setLastModifiedOn(new Date());
					let newFieldData = { ...initialFieldData };

					newFieldData.value = valueToPut;
					newFieldData.comment = comment;

					let fieldDataCopy = [...fieldData];
					setInitialFieldData(newFieldData);
					if (onFieldDataChange) {
						onFieldDataChange(data.id, { value: valueToPut, comment, projectFieldId: initialFieldData.id });
					}
					for (let i = 0; i < fieldDataCopy.length; i++) {
						if (fieldDataCopy[i].id === newFieldData.id) {
							fieldDataCopy[i] = newFieldData;
							setFieldData(fieldDataCopy);
							break;
						}
					}
					if (data.odoo_mapping && data.odoo_mapping !== "") {
						let mappingsClone = [...odooMappings];
						for (let i = 0; i < mappingsClone.length; i++) {
							if (data.id === mappingsClone[i].templatefieldid) {
								mappingsClone[i].odoo_value = valueToPut;
								mappingsClone[i].odoo_value = valueToPut;
								let newOdooData = { ...odooData };
								newOdooData["odoo_value"] = valueToPut;
								setOdooData(newOdooData);
								setCurrentOdooMappings([...mappingsClone]);
								break;
							}
						}
					}

					for (let i = 0; i < conditionalData.length; i++) {
						if (conditionalData[i].field_condition_id === newFieldData.field_id) {
							let conditionMet = false;
							if (conditionalData[i].condition_type === ">=") {
								if (parseInt(newFieldData.value) >= parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === ">") {
								if (parseInt(newFieldData.value) > parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === "=") {
								if (newFieldData.value == conditionalData[i].condition_value) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === "<") {
								if (parseInt(newFieldData.value) < parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							} else if (conditionalData[i].condition_type === "<=") {
								if (parseInt(newFieldData.value) <= parseInt(conditionalData[i].condition_value)) {
									conditionMet = true;
								}
							}
							let copyOfData = conditionalData;
							copyOfData[i].conditionMet = conditionMet;
							setConditionalData([...copyOfData]);
						}
					}
				}
			});
	};

	const sendCommentToDb = () => {
		if (initialFieldData && comment === initialFieldData.comment) {
			return;
		}
		if (!fieldDataExists) {
			postToDB(data.odoo_mapping && !odooUpdateFailed ? odooData.odoo_value : initialFieldData ? initialFieldData.value : "");
		} else {
			putToDB(data.odoo_mapping && !odooUpdateFailed ? odooData.odoo_value : initialFieldData.value);
		}
	};

	return (hasConditional && viewConditionMet) || !hasConditional || focusedFieldId === data.id ? (
		<div
			id={`field-${data.id}`}
			className={`${!showThisField && focusedFieldId !== data.id ? "hidden" : ""} ${
				focusedFieldId === data.id ? "rounded-lg ring-4 ring-blue-500 ring-offset-2" : ""
			}`}>
			<div
				className={`bg-white rounded-lg p-3 break-words  ${hasConditional ? "border border-2 border-green-800 ml-3" : ""} ${
					isGuest && data.public_editable ? "bg-yellow-100" : ""
				}`}>
				{errorMessage ? <ProjectErrorMessage message={errorMessage} setMessage={setErrorMessage} /> : null}
				<div>
					<div className="flex items-start justify-between gap-2">
						<p className="flex items-center font-bold">
							{isGuest ? null : data.public_editable ? <BiLink className="text-2xl" /> : null}
							{data.read_only || stage === "completed" ? <AiFillLock className="text-2xl" /> : null} {data.name}
						</p>
						{hasAerialView ? (
							<button
								type="button"
								className="flex shrink-0 items-center gap-1 rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"
								title={hasAerialMarker ? "Show this field's marker in Aerial View" : "Add this field to Aerial View"}
								onClick={() => onShowOnAerial(data.id)}>
								{hasAerialMarker ? "Show marker" : "Aerial View"}
							</button>
						) : null}
					</div>
					<p className="mb-2">{data.description}</p>
				</div>
				{data.field_type === "text" ? (
					<TextInput
						postToDB={postToDB}
						putToDB={putToDB}
						initialFieldData={initialFieldData}
						fieldDataExists={fieldDataExists}
						data={data}
						readOnly={data.read_only || stage === "completed" || (isGuest && !data.public_editable)}
						hasOdooMapping={data.odoo_mapping ? true : false}
						odooData={odooData}
					/>
				) : null}
				{data.field_type === "number" ? (
					<NumberInput
						postToDB={postToDB}
						putToDB={putToDB}
						initialFieldData={initialFieldData}
						fieldDataExists={fieldDataExists}
						readOnly={data.read_only || stage === "completed" || (isGuest && !data.public_editable)}
						hasOdooMapping={data.odoo_mapping ? true : false}
						odooData={odooData}
					/>
				) : null}
				{data.field_type === "long text" ? (
					<TextAreaInput
						postToDB={postToDB}
						putToDB={putToDB}
						initialFieldData={initialFieldData}
						fieldDataExists={fieldDataExists}
						readOnly={data.read_only || stage === "completed" || (isGuest && !data.public_editable)}
						hasOdooMapping={data.odoo_mapping ? true : false}
						odooData={odooData}
					/>
				) : null}
				{data.field_type === "date" ? (
					<DateInput
						postToDB={postToDB}
						putToDB={putToDB}
						initialFieldData={initialFieldData}
						fieldDataExists={fieldDataExists}
						readOnly={data.read_only || stage === "completed" || (isGuest && !data.public_editable)}
						hasOdooMapping={data.odoo_mapping ? true : false}
						odooData={odooData}
					/>
				) : null}
				{data.field_type === "selection" ? (
					<SelectionInput
						postToDB={postToDB}
						putToDB={putToDB}
						initialFieldData={initialFieldData}
						fieldDataExists={fieldDataExists}
						fieldSettings={fieldSettings}
						readOnly={data.read_only || stage === "completed" || (isGuest && !data.public_editable)}
						hasOdooMapping={data.odoo_mapping ? true : false}
						odooData={odooData}
					/>
				) : null}
				{data.field_type === "yes/no" ? (
					<BooleanInput
						postToDB={postToDB}
						putToDB={putToDB}
						initialFieldData={initialFieldData}
						fieldDataExists={fieldDataExists}
						fieldSettings={fieldSettings}
						readOnly={data.read_only || stage === "completed" || (isGuest && !data.public_editable)}
						hasOdooMapping={data.odoo_mapping ? true : false}
						odooData={odooData}
					/>
				) : null}
				{data.field_type === "currency" ? (
					<CurrencyInput
						postToDB={postToDB}
						putToDB={putToDB}
						initialFieldData={initialFieldData}
						fieldDataExists={fieldDataExists}
						readOnly={data.read_only || stage === "completed" || (isGuest && !data.public_editable)}
						hasOdooMapping={data.odoo_mapping ? true : false}
						odooData={odooData}
					/>
				) : null}

				{data.allow_comment ? (
					<div className="mt-2">
						<form>
							<label>
								Comment:
								{(isGuest && !data.public_editable) || stage === "completed" ? (
									<p className="border border-2 p-2 border-gray w-full whitespace-pre-wrap rounded-lg bg-gray-100">{comment || "\u00A0"}</p>
								) : (
									<textarea
										type="text"
										rows={4}
										value={comment}
										onChange={(e) => setComment(e.target.value)}
										onBlur={() => sendCommentToDb()}
										className="border border-2 p-2 border-gray w-full whitespace-pre-wrap rounded-lg"
										readOnly={stage === "completed" || (isGuest && !data.public_editable)}
									/>
								)}
							</label>
						</form>
					</div>
				) : null}
				{stage === "assigned" ? null : data.field_type === "photos" ? (
					<CameraContainer
						sectionName={sectionName}
						data={data}
						cameraid={data.id}
						api={api}
						token={token}
						setInitialFieldData={setInitialFieldData}
						fieldName={data.name}
						projectName={projectName}
						templateName={templateName}
						stage={stage}
						readOnly={stage === "completed" || (isGuest && !data.public_editable)}
						templateTypeId={templateTypeId}
						setHasPhotos={setHasPhotos}
						initialThumbnailPathes={initialThumbnailPathes.filter((filterData) => {
							const duplicatePattern = / \(\d+\)$/;
							let thumbnailName = filterData.name.split(".").slice(0, -1).join(".").trim();
							if (duplicatePattern.test(thumbnailName)) {
								thumbnailName = thumbnailName.replace(duplicatePattern, "");
							}
							let testFieldThmbName =
								sectionName.replaceAll("/", "-") +
								" - " +
								data.name
									.replaceAll("/", "_")
									.replaceAll("\\", "_")
									.replaceAll("<", "_")
									.replaceAll(">", "_")
									.replaceAll(":", "_")
									.replaceAll('"', "_")
									.replaceAll("|", "_")
									.replaceAll("?", "_")
									.replaceAll("*", "_")
									.replaceAll(".", "_")
									.trim();
							return testFieldThmbName.trim() === thumbnailName.trim();
						})}
						setLastModifiedOn={setLastModifiedOn}
						projectId={projectId}
						odooId={odooId}
						selectedOdooModel={selectedOdooModel}
						odooInstallationName={odooInstallationName}
						odooParentName={odooParentName}
						inSQS={inSQS}
						setInSQS={setInSQS}
						startingPendingImages={startingPendingImages}
					/>
				) : data.field_type !== "photos" && data.allow_photos ? (
					<CameraContainer
						sectionName={sectionName}
						data={data}
						cameraid={data.id}
						api={api}
						token={token}
						fieldName={data.name}
						projectName={projectName}
						templateName={templateName}
						stage={stage}
						readOnly={stage === "completed" || (isGuest && !data.public_editable)}
						templateTypeId={templateTypeId}
						setHasPhotos={setHasPhotos}
						initialThumbnailPathes={initialThumbnailPathes.filter((filterData) => {
							const duplicatePattern = / \(\d+\)$/;
							let thumbnailName = filterData.name.split(".").slice(0, -1).join(".").trim();
							if (duplicatePattern.test(thumbnailName)) {
								thumbnailName = thumbnailName.replace(duplicatePattern, "");
							}
							let testFieldThmbName =
								sectionName.replaceAll("/", "-") +
								" - " +
								data.name
									.trim()
									.replaceAll("/", "_")
									.replaceAll("\\", "_")
									.replaceAll("<", "_")
									.replaceAll(">", "_")
									.replaceAll(":", "_")
									.replaceAll('"', "_")
									.replaceAll("|", "_")
									.replaceAll("?", "_")
									.replaceAll("*", "_")
									.replaceAll(".", "_")
									.trim();
							return testFieldThmbName.trim() === thumbnailName.trim();
						})}
						setLastModifiedOn={setLastModifiedOn}
						projectId={projectId}
						odooId={odooId}
						selectedOdooModel={selectedOdooModel}
						odooInstallationName={odooInstallationName}
						odooParentName={odooParentName}
						inSQS={inSQS}
						setInSQS={setInSQS}
						startingPendingImages={startingPendingImages}
					/>
				) : null}
				{!isGuest && odooData && odooData.odoo_value === "failedtogetdataforpmscode400" ? (
					<div className="flex items-center gap-2 bg-blue-300 p-2 mt-2 rounded-lg">
						<BsShieldExclamation color="black" className="text-3xl" />
						<p>
							This field's odoo mapping may be broken or not set up properly. Please double check that it works. Data will get saved to Krummy
							Capture but may not appear in Odoo as a result of this.
						</p>
					</div>
				) : null}
				{data.allow_comment && data.comment_has_validation && (!initialFieldData || (initialFieldData && initialFieldData.value !== comment)) ? (
					<div className="flex items-center gap-2 bg-blue-400 p-2 mt-2 rounded-lg">
						<BsShieldExclamation color="black" className="text-3xl" />
						<p>The comment and field data must match.</p>
					</div>
				) : null}
				{data.required && data.field_type !== "photos" && !metRequireCondition ? (
					!isGuest && odooData && odooData.odoo_value === "failedtogetdataforpmscode400" && initialFieldData && initialFieldData.value ? (
						<div className="flex items-center gap-2 bg-rose-100 p-2 mt-2 rounded-lg">
							<BsExclamationCircleFill color="red" className="text-3xl" />
							<p>This is a required field. Please enter a value. (The data is in Krummy Capture but it may have failed to send to Odoo.)</p>
						</div>
					) : (
						<div className="flex items-center gap-2 bg-rose-100 p-2 mt-2 rounded-lg">
							<BsExclamationCircleFill color="red" className="text-3xl" />
							<p>This is a required field. Please enter a value.</p>
						</div>
					)
				) : null}
				{/* {(data.require_photos && hasPhotos === false) || (data.required && data.field_type === "photos" && !hasPhotos)  || (data.allow_photos && hasPhotos === false) ? ( */}
				{(data.require_photos && hasPhotos === false) || (data.required && data.field_type === "photos" && !hasPhotos) ? (
					<div className="flex items-center gap-2 bg-yellow-300 p-2 mt-2 rounded-lg">
						<BsFillCameraFill color="black" className="text-3xl" />
						<p>Photos are required. Take at least one photo.</p>
					</div>
				) : null}
				{odooUpdateFailed ? (
					<div className="flex items-center gap-2 bg-blue-100 p-2 mt-2 rounded-lg">
						<FcInfo className="text-3xl" />
						<p>There is an error with the odoo mapping. Data will not be sent to Odoo.</p>
					</div>
				) : null}
				{data.required && data.require_comment && !metRequireCommentCondition ? (
					<div className="flex items-center gap-2 bg-rose-100 p-2 mt-2 rounded-lg">
						<BsExclamationCircleFill color="red" className="text-3xl" />
						<p>Comment is required for this section.</p>
					</div>
				) : null}
				{aerialMarkersReady && data.require_aerial_view_marker && (!hasConditional || viewConditionMet) && !hasAerialMarker ? (
					<div className="flex items-center gap-2 bg-orange-400 p-2 mt-2 rounded-lg">
						<FaMapMarkerAlt color="black" className="text-3xl" />
						<p>An aerial view marker is required. Add a marker in Aerial View.</p>
					</div>
				) : null}
			</div>
		</div>
	) : null;
};

export default FieldConverter;
